import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Tag, Button, Input, Select, Space, Tooltip, Alert, Empty,
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, EyeOutlined, WarningOutlined,
  ClockCircleOutlined, CheckCircleOutlined, FileTextOutlined,
  MedicineBoxOutlined, CarOutlined, FileSearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, libelleTypeExpertise } from '../../variables/constants';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';

const GREEN = '#1a6b3c';
const DASH = <span style={{ color: '#9ca3af' }}>-</span>;

/* Mapping statut affectation → couleur + libellé */
const STATUT_META = avecLibelleTraduit({
  EN_ATTENTE: { color: 'orange' },
  RAPPORT_RECU: { color: 'blue' },
  CLOTURE: { color: 'green' },
}, 'expertises:statuts');

const TYPE_EXPERT_META = avecLibelleTraduit({
  MEDICAL: { icon: <MedicineBoxOutlined /> },
  AUTOMOBILE: { icon: <CarOutlined /> },
}, 'expertises:typesExpert');



/* Calcule l'état temporel d'une affectation par rapport à sa date limite. */
function computeRespect(aff) {
  if (!aff.dateLimiteRapport) return { tone: 'neutral', label: i18n.t('expertises:respect.sansEcheance') };
  const limite = dayjs(aff.dateLimiteRapport);
  if (aff.statut === 'RAPPORT_RECU' || aff.statut === 'CLOTURE') {
    return { tone: 'success', label: i18n.t('expertises:respect.rapportRecu'), icon: <CheckCircleOutlined /> };
  }
  const jours = limite.diff(dayjs(), 'day');
  if (jours < 0) return { tone: 'danger', label: i18n.t('expertises:respect.enRetard', { jours: Math.abs(jours) }), icon: <WarningOutlined /> };
  if (jours <= 3) return { tone: 'warn', label: i18n.t('expertises:respect.echeanceDans', { jours }), icon: <ClockCircleOutlined /> };
  return { tone: 'neutral', label: i18n.t('expertises:respect.dans', { jours }), icon: <ClockCircleOutlined /> };
}

const TONE_STYLE = {
  success: { bg: '#ecfdf5', color: '#15803d', border: '#bbf7d0' },
  warn: { bg: '#fff7ed', color: '#b45309', border: '#fed7aa' },
  danger: { bg: '#fef2f2', color: '#b42318', border: '#fecaca' },
  neutral: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
};

function RespectPill({ aff }) {
  const r = computeRespect(aff);
  const s = TONE_STYLE[r.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {r.icon}{r.label}
    </span>
  );
}

export default function ListExpertises() {
  const { t } = useTranslation('expertises');
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statut, setStatut] = useState('');
  const [typeExpert, setTypeExpert] = useState('');
  const [enRetard, setEnRetard] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (page = 1, size = 25) => {
    setLoading(true);
    try {
      const params = {
        page: Math.max(page - 1, 0),
        size,
      };
      if (debounced) params.search = debounced;
      if (statut) params.statut = statut;
      if (typeExpert) params.typeExpert = typeExpert;
      if (enRetard) params.enRetard = true;

      const res = await axiosInstance.get('/v1/affectations-expert', { params });
      const body = res.data || {};
      setData(Array.isArray(body.data) ? body.data : []);
      setPagination({
        current: (body.currentPage ?? 0) + 1,
        pageSize: body.pageSize || size,
        total: body.totalElements || 0,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || t('messages.erreurChargement'));
      setData([]);
      setPagination(p => ({ ...p, total: 0 }));
    } finally { setLoading(false); }
  }, [debounced, statut, typeExpert, enRetard, t]);

  useEffect(() => {
    load(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  /* Reset page lorsque un filtre change */
  useEffect(() => { setPagination(p => ({ ...p, current: 1 })); }, [debounced, statut, typeExpert, enRetard]);

  /* Compteurs synthétiques sur la page courante (informatif). */
  const stats = useMemo(() => {
    const total = pagination.total;
    const visible = data.length;
    const recus = data.filter(a => a.statut === 'RAPPORT_RECU' || a.statut === 'CLOTURE').length;
    const enAttente = data.filter(a => a.statut === 'EN_ATTENTE').length;
    const retards = data.filter(a =>
      a.statut === 'EN_ATTENTE' && a.dateLimiteRapport && dayjs(a.dateLimiteRapport).isBefore(dayjs(), 'day')
    ).length;
    return { total, visible, recus, enAttente, retards };
  }, [data, pagination.total]);

  const columns = [
    {
      title: t('colonnes.dossier'), key: 'sinistre', width: 170, fixed: 'left',
      render: (_, r) => (
        <button
          type="button"
          onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: GREEN, fontWeight: 700, fontSize: 13,
            fontFamily: 'var(--font-main)', cursor: 'pointer', letterSpacing: '0.02em',
          }}
          title={t('colonnes.voirSinistre')}
        >
          {r.numeroSinistreLocal || '-'}
        </button>
      ),
    },
    {
      title: t('colonnes.victime'), dataIndex: 'victimeNomPrenom', key: 'victime',
      render: (v) => v ? <span style={{ fontWeight: 600 }}>{v}</span> : DASH,
    },
    {
      title: t('colonnes.expert'), key: 'expert',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{r.expertNomComplet || '-'}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {TYPE_EXPERT_META[r.expertTypeLabel]?.icon}{' '}
            {TYPE_EXPERT_META[r.expertTypeLabel]?.label || r.expertTypeLabel || ''}
          </span>
        </Space>
      ),
    },
    {
      title: t('colonnes.type'), dataIndex: 'typeExpertise', key: 'typeExpertise', width: 130,
      render: (v) => v ? <Tag style={{ borderRadius: 20 }}>{libelleTypeExpertise(v)}</Tag> : DASH,
    },
    {
      title: t('colonnes.affectation'), dataIndex: 'dateAffectation', key: 'dateAffectation', width: 110,
      render: (v) => v ? formatDate(v) : DASH,
    },
    {
      title: t('colonnes.limite'), dataIndex: 'dateLimiteRapport', key: 'dateLimiteRapport', width: 110,
      render: (v) => v ? formatDate(v) : DASH,
    },
    {
      title: t('colonnes.echeance'), key: 'respect', width: 160,
      render: (_, r) => <RespectPill aff={r} />,
    },
    {
      title: t('colonnes.statut'), dataIndex: 'statut', key: 'statut', width: 140,
      render: (v) => {
        const m = STATUT_META[v] || { color: 'default', label: v };
        return <Tag color={m.color} style={{ borderRadius: 20, fontWeight: 600 }}>{m.label}</Tag>;
      },
    },
    {
      title: '', key: 'actions', width: 80, align: 'right',
      render: (_, r) => (
        <Tooltip title={t('colonnes.ouvrirDossier')}>
          <Button
            size="small" icon={<EyeOutlined />}
            onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
            style={{ borderColor: GREEN, color: GREEN }}
          />
        </Tooltip>
      ),
    },
  ];

  const reset = () => {
    setSearch(''); setStatut(''); setTypeExpert(''); setEnRetard(false);
  };

  return (
    <Layout title={t('titre')}>
      <div style={{ marginBottom: 16 }}>
        {/* `pagination.total` = totalElements du backend : les cartes KPI de cet ecran
            distinguent deja explicitement le total des compteurs « (page) », donc le
            seul compte global disponible est celui-la. */}
        <PageHeader
          titre={t('titre')}
          icone={<FileSearchOutlined />}
          compte={pagination.total}
          unite={t('unite')}
        />

        {/* Cartes statistiques */}
        <KpiCards items={[
          { label: t('kpi.affectations'), value: stats.total, color: GREEN, icon: <FileTextOutlined /> },
          { label: t('kpi.enAttente'), value: stats.enAttente, color: '#b45309', icon: <ClockCircleOutlined /> },
          { label: t('kpi.rapportRecu'), value: stats.recus, color: '#15803d', icon: <CheckCircleOutlined /> },
          { label: t('kpi.enRetard'), value: stats.retards, color: '#b42318', icon: <WarningOutlined /> },
        ]} />

        {/* Filtres */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8,
          padding: 12, background: '#fff',
          border: '1px solid #e7e9ee', borderRadius: 10, marginBottom: 12,
        }}>
          <Input
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('filtres.recherche')}
            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            style={{ width: 320, borderRadius: 8 }}
          />
          <Select
            allowClear
            value={statut || undefined}
            onChange={(v) => setStatut(v || '')}
            placeholder={t('colonnes.statut')}
            style={{ width: 180 }}
            options={[
              { value: 'EN_ATTENTE', label: t('statuts.EN_ATTENTE') },
              { value: 'RAPPORT_RECU', label: t('statuts.RAPPORT_RECU') },
              { value: 'CLOTURE', label: t('statuts.CLOTURE') },
            ]}
          />
          <Select
            allowClear
            value={typeExpert || undefined}
            onChange={(v) => setTypeExpert(v || '')}
            placeholder={t('filtres.typeExpert')}
            style={{ width: 180 }}
            options={[
              { value: 'MEDICAL', label: <span><MedicineBoxOutlined /> {t('typesExpert.MEDICAL')}</span> },
              { value: 'AUTOMOBILE', label: <span><CarOutlined /> {t('typesExpert.AUTOMOBILE')}</span> },
            ]}
          />
          <Button
            type={enRetard ? 'primary' : 'default'}
            icon={<WarningOutlined />}
            onClick={() => setEnRetard(v => !v)}
            danger={enRetard}
            style={enRetard ? {} : { borderColor: '#fecaca', color: '#b42318' }}
          >
            {t('filtres.enRetardUniquement')}
          </Button>
          <Button onClick={reset} icon={<ReloadOutlined />}>{t('filtres.reinitialiser')}</Button>
          <span style={{ flex: 1 }} />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => load(pagination.current, pagination.pageSize)}
          >
            {t('filtres.actualiser')}
          </Button>
        </div>

        <Table
          dataSource={data}
          columns={columns}
          rowKey="affectationTrackingId"
          loading={loading}
          size="middle"
          scroll={{ x: 1100 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (total, range) => t('table.plage', { debut: range[0], fin: range[1], total }),
            onChange: (page, size) => load(page, size),
          }}
          locale={{
            emptyText: <Empty
              description={
                <span>
                  {(statut || typeExpert || enRetard || debounced) ? t('vide.filtres') : t('vide.aucune')}
                </span>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />,
          }}
        />

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16, borderRadius: 10 }}
          message={t('aVenir.titre')}
          description={
            <span style={{ fontSize: 12.5 }}>
              {t('aVenir.description')}
            </span>
          }
        />
      </div>
    </Layout>
  );
}

